/**
 * CompactSelect — label-only collapsed dropdown.
 *
 * Visual language matches ProviderPicker (Phase 1 tokens):
 *   bg-black/30 border border-white/10 rounded-lg
 *
 * Use this for Duration, Video Style, Resolution — any field whose
 * options are plain labels with no pricing/badges/quality stars.
 * For rich-metadata options use <ProviderPicker> instead.
 *
 * API:
 *   <CompactSelect
 *     options={[{ value: '1', label: '30 seconds' }]}
 *     value={selected}
 *     onChange={setSelected}
 *     label="Duration"
 *     icon={<Clock className="h-4 w-4" />}
 *     searchable  // auto when options.length >= 15
 *   />
 *
 * Keyboard:
 *   ↑/↓  move highlight   Enter  select highlighted   Esc  close   Tab  close + blur
 *
 * Mobile (360px):
 *   Dropdown is capped at max-h-72 with overflow-y-auto.
 *   Constrained to max-w-[calc(100vw-32px)] so it never causes horizontal scroll.
 *   Each row min-h-[44px] per RULE #13.
 */

import { useState, useRef, useEffect, useCallback, useId, memo, type KeyboardEvent } from 'react';
import { ChevronDown, ChevronUp, Check, Search } from 'lucide-react';
import { useClickOutside } from '@/hooks/useClickOutside';
import { useTranslation } from '@/hooks/useTranslation';
import type { CompactSelectProps, CompactOption } from './types';

// =============================================================================
// HELPER — filter options by search query
// =============================================================================

function filterOptions(options: CompactOption[], query: string): CompactOption[] {
  if (!query.trim()) return options;
  const lower = query.toLowerCase();
  return options.filter(
    (o) => o.label.toLowerCase().includes(lower) || (o.hint ?? '').toLowerCase().includes(lower)
  );
}

// =============================================================================
// COMPONENT
// =============================================================================

function CompactSelectInner({
  options,
  value,
  onChange,
  label,
  icon,
  placeholder,
  disabled = false,
  className = '',
  searchable,
  testId,
  error,
  helpText,
}: CompactSelectProps) {
  const { t } = useTranslation();
  const resolvedPlaceholder = placeholder ?? t('common.compactSelect.selectPlaceholder');
  const uid = useId();
  const triggerId = `compact-select-trigger-${uid}`;
  const listboxId = `compact-select-listbox-${uid}`;

  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);

  const containerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const isSearchable = searchable !== undefined ? searchable : options.length >= 15;

  // Filtered options
  const filtered = isSearchable ? filterOptions(options, searchQuery) : options;

  // Find the currently selected option label for the trigger
  const selectedOption = options.find((o) => o.value === value);

  // ==========================================================================
  // Click-outside — same pattern as ProviderPicker
  // ==========================================================================

  useClickOutside(containerRef, () => setIsOpen(false), { enabled: isOpen });

  useEffect(() => {
    if (isOpen) {
      // Focus search input if searchable
      if (isSearchable && searchRef.current) {
        searchRef.current.focus();
      }
    }
  }, [isOpen, isSearchable]);

  // Clear search when closing
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
      setHighlightedIndex(-1);
    }
  }, [isOpen]);

  // ==========================================================================
  // Reset highlight when filtered list changes
  // ==========================================================================
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [searchQuery]);

  // ==========================================================================
  // Scroll highlighted item into view
  // ==========================================================================
  useEffect(() => {
    if (highlightedIndex < 0 || !listRef.current) return;
    const item = listRef.current.children[highlightedIndex] as HTMLElement | undefined;
    item?.scrollIntoView({ block: 'nearest' });
  }, [highlightedIndex]);

  // ==========================================================================
  // Handlers
  // ==========================================================================

  const open = useCallback(() => {
    if (!disabled) setIsOpen(true);
  }, [disabled]);

  const close = useCallback(() => setIsOpen(false), []);

  const handleSelect = useCallback(
    (option: CompactOption) => {
      if (option.disabled) return;
      onChange(option.value);
      setIsOpen(false);
    },
    [onChange]
  );

  const handleTriggerKeyDown = useCallback(
    (e: KeyboardEvent<HTMLButtonElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        open();
      }
    },
    [open]
  );

  const handleListKeyDown = useCallback(
    (e: KeyboardEvent<HTMLElement>) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setHighlightedIndex((prev) => {
            const next = prev + 1;
            return next >= filtered.length ? 0 : next;
          });
          break;
        case 'ArrowUp':
          e.preventDefault();
          setHighlightedIndex((prev) => {
            const next = prev - 1;
            return next < 0 ? filtered.length - 1 : next;
          });
          break;
        case 'Enter':
          e.preventDefault();
          if (highlightedIndex >= 0 && filtered[highlightedIndex]) {
            handleSelect(filtered[highlightedIndex]);
          }
          break;
        case 'Escape':
        case 'Tab':
          close();
          break;
        default:
          break;
      }
    },
    [filtered, highlightedIndex, handleSelect, close]
  );

  // ==========================================================================
  // Render
  // ==========================================================================

  return (
    <div className={`space-y-1 ${className}`} ref={containerRef}>
      {/* Label */}
      {label && (
        <label
          htmlFor={triggerId}
          className="form-label flex items-center gap-2 text-sm font-medium text-white/70"
        >
          {icon && <span aria-hidden="true">{icon}</span>}
          {label}
        </label>
      )}

      {/* Trigger + Dropdown — relative container so dropdown overlays correctly */}
      <div className="relative">
        {/* Trigger */}
        <button
          id={triggerId}
          type="button"
          role="combobox"
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-controls={listboxId}
          aria-label={label}
          disabled={disabled}
          onClick={isOpen ? close : open}
          onKeyDown={handleTriggerKeyDown}
          data-testid={testId}
          className={[
            'w-full flex items-center gap-2 px-3 py-2 min-h-[44px]',
            'border rounded-lg transition-all text-left',
            error
              ? 'border-red-500/60 bg-black/20'
              : 'border-white/10 bg-black/20 hover:border-white/20 hover:bg-black/30',
            disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
          ]
            .join(' ')
            .trim()}
        >
          {/* Icon (no label prop) */}
          {!label && icon && (
            <span aria-hidden="true" className="text-white/50 shrink-0">
              {icon}
            </span>
          )}

          {/* Selected label */}
          <span className="flex-1 text-sm text-white truncate">
            {selectedOption ? (
              selectedOption.label
            ) : (
              <span className="text-white/40">{resolvedPlaceholder}</span>
            )}
          </span>

          {/* Hint */}
          {selectedOption?.hint && (
            <span className="text-xs text-white/40 shrink-0 hidden sm:block">
              {selectedOption.hint}
            </span>
          )}

          {/* Chevron */}
          {isOpen ? (
            <ChevronUp className="h-4 w-4 text-white/40 shrink-0" aria-hidden="true" />
          ) : (
            <ChevronDown className="h-4 w-4 text-white/40 shrink-0" aria-hidden="true" />
          )}
        </button>

        {/* Dropdown — absolute so it overlays content below without layout shift */}
        {isOpen && (
          <div
            className={[
              'absolute top-full left-0 right-0 mt-1 z-50',
              'border border-white/10 rounded-lg',
              // Solid surface (matches aurora theme) so the menu fully occludes
              // content behind it. bg-black/30 + backdrop-blur looked semi-
              // transparent here because nested backdrop-filters don't stack —
              // overlapping panels (e.g. Media Type cards) bled through.
              'bg-[#1a0830] shadow-xl',
              'animate-in slide-in-from-top-1 duration-150',
              'max-w-[calc(100vw-32px)]',
            ]
              .join(' ')
              .trim()}
            onKeyDown={handleListKeyDown}
          >
            {/* Search input */}
            {isSearchable && (
              <div className="px-3 pt-3 pb-2 border-b border-white/5">
                <div className="flex items-center gap-2 px-2 py-1.5 rounded-md bg-black/30 border border-white/10">
                  <Search className="h-3.5 w-3.5 text-white/40 shrink-0" aria-hidden="true" />
                  <input
                    ref={searchRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={handleListKeyDown}
                    placeholder={t('common.compactSelect.searchPlaceholder')}
                    aria-label={t('common.compactSelect.searchAria')}
                    className="flex-1 bg-transparent text-sm text-white placeholder-white/30 outline-none min-w-0"
                  />
                </div>
              </div>
            )}

            {/* Options list */}
            <ul
              id={listboxId}
              ref={listRef}
              role="listbox"
              aria-label={label}
              tabIndex={isSearchable ? -1 : 0}
              onKeyDown={!isSearchable ? handleListKeyDown : undefined}
              className="max-h-72 overflow-y-auto py-1 outline-none"
            >
              {filtered.length === 0 ? (
                <li className="px-3 py-3 text-sm text-white/40 text-center">
                  {t('common.compactSelect.noOptions')}
                </li>
              ) : (
                filtered.map((option, index) => {
                  const isSelected = option.value === value;
                  const isHighlighted = index === highlightedIndex;
                  return (
                    <li
                      key={option.value}
                      role="option"
                      aria-selected={isSelected}
                      aria-disabled={option.disabled}
                      title={option.disabled ? option.disabledReason : undefined}
                      onClick={() => handleSelect(option)}
                      onMouseEnter={() => setHighlightedIndex(index)}
                      className={[
                        'flex items-center gap-2 px-3 min-h-[44px] cursor-pointer transition-colors text-sm',
                        isHighlighted ? 'bg-white/5' : '',
                        isSelected ? 'text-primary-300' : 'text-white/80',
                        option.disabled ? 'opacity-40 cursor-not-allowed' : '',
                      ]
                        .join(' ')
                        .trim()}
                    >
                      <span className="flex-1 truncate">{option.label}</span>
                      {option.hint && (
                        <span className="text-xs text-white/40 shrink-0">{option.hint}</span>
                      )}
                      {isSelected && (
                        <Check
                          className="h-3.5 w-3.5 text-primary-400 shrink-0"
                          aria-hidden="true"
                        />
                      )}
                    </li>
                  );
                })
              )}
            </ul>
          </div>
        )}
      </div>
      {/* end relative wrapper */}

      {/* Help / Error text */}
      {!isOpen && error && <p className="text-sm text-red-400 mt-1">{error}</p>}
      {!isOpen && !error && helpText && <p className="text-sm text-white/50 mt-1">{helpText}</p>}
    </div>
  );
}

export const CompactSelect = memo(CompactSelectInner);
export default CompactSelect;
